import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { CustomizeContentLegacy } from '../-funnel-components/customize-content-legacy';
import { CustomizeContentV2 } from '../-funnel-components/customize-content-v2';
import { BlymLoading } from '../../../../components/loading';
import { type ContentType } from '../../../../entity/api/content-funnel';
import { PlanFeature } from '../../../../entity/api/plan';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { captureException } from '../../../../logger/capture-exception';
import { getQueryClient } from '../../../../sdk/request';
import {
  fetchArticleByIdImperative,
  getArticleAnalysisFromCache,
  getAuthUserFromCache,
  useArticleFunnelByArticleId,
} from '../../../../services/storage';
import { getOrganizationBillingFromCache } from '../../../../services/storage/organization-billing-queries';
import { getSelectedBrand } from '../../../../shared/brand';
import { Feature, isFeatureAvailable } from '../../../../shared/feature';
import { getSelectedOrganization } from '../../../../shared/organization';
import { hasOrgFeature } from '../../../../shared/subscription';

// Legacy form type (old version)
export type CustomizeContentFormLegacy = {
  brandContext: boolean;
  contentDirection: string;
  outlineStructure: string;
  hasKeyTakeaways: boolean;
  hasFaq: boolean;
};

// New form type (V2)
export type CustomizeContentFormV2 = {
  contentType: ContentType;
  contentDirection: string;
  outlineStructure: string;
  hasKeyTakeaways: boolean;
  hasFaq: boolean;
  brandContext: boolean;
  mentionBrandInIntroduction: boolean;
  mentionBrandInBody: boolean;
  mentionBrandInConclusion: boolean;
  isWordCountAiGenerated: boolean;
  wordCount?: number;
};

// Keep backward compatibility export
export type CustomizeContentForm = CustomizeContentFormLegacy;

export const Route = createFileRoute('/app/funnel/$id/_layout-funnel/customize-content')({
  beforeLoad: async function (data: { params: { id: string } }) {
    addTrackCrumb({
      category: 'route',
      message: '/app/funnel/$id/_layout-funnel/customize-content',
      level: 'info',
    });
    addTrackCrumb({
      category: 'funnel',
      message: 'entering funnel customize content',
      level: 'info',
    });
    const { id } = data.params;
    const queryClient = getQueryClient();

    try {
      await fetchArticleByIdImperative(queryClient, id);
    } catch (err) {
      captureException(
        err,
        {
          file: '_layout-funnel.customize-content.tsx',
          method: 'loader',
          infoMessage: 'unable to get article by id',
          contentId: id,
        },
        trackError
      );

      throw err;
    }
  },
  // eslint-disable-next-line no-use-before-define
  component: CustomizeContent,
});

function CustomizeContent() {
  const { id } = Route.useParams() as unknown as { id: string };
  const [isLoading, setIsLoading] = useState(false);

  // TanStack Query hook for auth user
  const queryClient = getQueryClient();
  const authUser = getAuthUserFromCache(queryClient);
  const currentBrand = getSelectedBrand(authUser);
  const currentOrganization = getSelectedOrganization(authUser);

  // TanStack Query hooks
  const articleFunnelQuery = useArticleFunnelByArticleId(id);
  const articleFunnel = articleFunnelQuery.data ?? null;
  const articleAnalysis = getArticleAnalysisFromCache(queryClient, id);
  const { averageWordsPerPage } = articleAnalysis ?? {};
  const orgBilling = getOrganizationBillingFromCache(queryClient, currentOrganization?._id ?? '');

  const hasSupportingMaterials = hasOrgFeature(orgBilling, PlanFeature.SupportingMaterials);

  if (articleFunnel == null) {
    return (
      <div className="relative flex flex-1 items-center justify-center overflow-hidden pb-14">
        <BlymLoading />
      </div>
    );
  }

  // Feature flag check to switch between V2 and Legacy
  if (isFeatureAvailable(Feature.FUNNEL_STEP_ONE_V2)) {
    return (
      <CustomizeContentV2
        articleFunnel={articleFunnel}
        currentBrand={currentBrand}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        averageWordsPerPage={averageWordsPerPage}
        hasSupportingMaterials={hasSupportingMaterials}
      />
    );
  }

  return (
    <CustomizeContentLegacy
      articleFunnel={articleFunnel}
      currentBrand={currentBrand}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
    />
  );
}
